import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import Button from "../Buttons/Button";
import { ClipboardList } from "lucide-react";
import { NOTIFICATIONS_FLOATING_BUTTON_SIZE } from "../../hooks/useNotificationsFabPosition";
import {
  GRAY_500,
  BOX_SHADOW_LITTLE,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";

const notificationPing = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.55;
  }
  75%, 100% {
    transform: scale(2.4);
    opacity: 0;
  }
`;

const ProcessingIndicator = () => (
  <Box
    sx={{
      position: "relative",
      width: 10,
      height: 10,
      flexShrink: 0,
      overflow: "visible",
      mt: 0.1,
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        backgroundColor: "#4ade80",
        animation: `${notificationPing} 2s ease-out infinite`,
      }}
    />
    <Box
      sx={{
        position: "relative",
        width: 8,
        height: 8,
        top: 1,
        left: 1,
        borderRadius: "50%",
        backgroundColor: "#4ade80",
        boxShadow: "0 0 10px 2px rgba(74, 222, 128, 0.45)",
      }}
    />
  </Box>
);

const FabProcessingIndicator = () => (
  <Box
    sx={{
      position: "relative",
      width: 10,
      height: 10,
      flexShrink: 0,
      overflow: "visible",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        backgroundColor: "#4ade80",
        animation: `${notificationPing} 2s ease-out infinite`,
      }}
    />
    <Box
      sx={{
        position: "relative",
        width: 8,
        height: 8,
        top: 1,
        left: 1,
        borderRadius: "50%",
        backgroundColor: "#4ade80",
        boxShadow: "0 0 10px 2px rgba(74, 222, 128, 0.45)",
      }}
    />
  </Box>
);

/**
 * Notifications button component with active processing indicator
 * @param {Object} props
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.hasActiveProcessing - Whether there are active processing alerts
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {"default"|"fab"} props.variant - Button visual variant
 * @param {Object} props.sx - Additional sx styles
 */
export const NotificationsButton = ({
  onClick,
  hasActiveProcessing,
  disabled = false,
  variant = "default",
  dragHandlers = {},
  isDragging = false,
  sx = {},
  ...props
}) => {
  const isFab = variant === "fab";

  if (isFab) {
    return (
      <Button
        color="secondary"
        iconOnly
        onClick={onClick}
        disabled={disabled}
        {...dragHandlers}
        sx={{
          width: NOTIFICATIONS_FLOATING_BUTTON_SIZE,
          minWidth: `${NOTIFICATIONS_FLOATING_BUTTON_SIZE}px !important`,
          height: `${NOTIFICATIONS_FLOATING_BUTTON_SIZE}px !important`,
          maxHeight: `${NOTIFICATIONS_FLOATING_BUTTON_SIZE}px !important`,
          p: "0 !important",
          borderRadius: "50%",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          boxShadow: BOX_SHADOW_LITTLE,
          backgroundColor: "background.paper !important",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 0,
          touchAction: "none",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          "&.MuiButtonBase-root.MuiButton-root:hover": {
            backgroundColor: "background.paper !important",
            border: `1px solid ${INPUT_COLOR_BORDER} !important`,
            boxShadow: BOX_SHADOW_LITTLE,
            color: "text.primary !important",
          },
          ...sx,
        }}
        {...props}
      >
        {hasActiveProcessing && !disabled ? (
          <FabProcessingIndicator />
        ) : (
          <ClipboardList size={16} color={GRAY_500} strokeWidth={2} />
        )}
      </Button>
    );
  }

  return (
    <Button
      color="secondary"
      onClick={onClick}
      disabled={disabled}
      sx={{
        textTransform: "none",
        height: 42,
        maxHeight: 42,
        minWidth: "max-content",
        px: "10px !important",
        mr: 0.5,
        border: "none",
        boxShadow: "none",
        backgroundColor: "transparent !important",
        "&.MuiButtonBase-root.MuiButton-root:hover": {
          backgroundColor: "transparent !important",
          border: "none !important",
          boxShadow: "none !important",
          color: "inherit !important",
        },
        ...sx,
      }}
      {...props}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {hasActiveProcessing && !disabled ? (
          <ProcessingIndicator />
        ) : (
          <ClipboardList size={20} color={GRAY_500} />
        )}
      </Box>
    </Button>
  );
};

export default NotificationsButton;
